import { computed, DestroyRef, Injectable, Signal, signal } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, shareReplay, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AccountGroup,
  AccountGroupApiService,
  LookupRequestInterface,
  ProjectionFilter,
} from '@vendasta/account-group';
import { DialogData } from '@vendasta/businesses';
import { debounceTime, map } from 'rxjs/operators';

const PAGE_SIZE = 20;

@Injectable({
  providedIn: 'root',
})
export class BusinessSelectorModalService {
  private readonly nextCursor$$ = new BehaviorSubject<string>('');
  private readonly searchFormFieldValue$$ = new BehaviorSubject<string>('');
  private readonly loadMore$$ = new BehaviorSubject<boolean>(false);
  private readonly loadMoreTrigger$ = this.loadMore$$.asObservable().pipe(map(() => null));
  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  public readonly loading = toSignal(this.loading$$);
  private readonly business$$ = new BehaviorSubject<AccountGroup[]>([]);
  public readonly business = toSignal(this.business$$);
  private readonly hasMore$$ = new BehaviorSubject<boolean>(false);
  public readonly hasMore = toSignal(this.hasMore$$);
  private _loadingFirstPage = signal(true);
  public readonly loadingFirstPage: Signal<boolean> = computed(() => this._loadingFirstPage());

  private previousSearchTerm = '';
  public data: DialogData;

  constructor(
    private readonly accountGroupService: AccountGroupApiService,
    private readonly destroyRef: DestroyRef,
  ) {}

  public initializeBusinesses(): void {
    combineLatest([this.searchFormFieldValue$$, this.loadMoreTrigger$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        distinctUntilChanged(),
        shareReplay(1),
        switchMap(([searchTerm]) => {
          this.loading$$.next(true);
          let cursor = this.nextCursor$$.value;
          if (this.previousSearchTerm !== searchTerm) {
            cursor = '';
            this.nextCursor$$.next('');
            this._loadingFirstPage.set(true);
            this.business$$.next([]);
          }
          this.previousSearchTerm = searchTerm;
          const projectionFilter =
            this.data.projectionFilterOverride ||
            new ProjectionFilter({
              napData: true,
              accountGroupExternalIdentifiers: true,
              constraints: this.data.partnerId === 'VMF',
            });
          const lookupRequest: LookupRequestInterface = {
            projectionFilter,
            filters: {
              partnerId: this.data.partnerId,
              includeDeleted: false,
              marketIds: Array.isArray(this.data.marketId) ? this.data.marketId : [this.data.marketId],
            },
            pageSize: PAGE_SIZE,
            searchTerm,
            cursor,
            sortOptions: { direction: 1, field: 1 },
          };
          return this.accountGroupService.lookup(lookupRequest);
        }),
      )
      .subscribe({
        next: (pagedAccountGroupResponse) => {
          const businesses = pagedAccountGroupResponse.accountGroups ?? [];
          this.business$$.next(this.business$$.value.concat(businesses));
          this.hasMore$$.next(pagedAccountGroupResponse.hasMore);
          this.nextCursor$$.next(pagedAccountGroupResponse.nextCursor);
          this.loading$$.next(false);
          this._loadingFirstPage.set(false);
        },
        error: () => {
          this.loading$$.next(false);
          this._loadingFirstPage.set(false);
        },
      });
  }

  set searchFormFieldValue(value: string) {
    this.searchFormFieldValue$$.next(value);
  }

  set loadMore(value: boolean) {
    this.loadMore$$.next(value);
  }

  public resetState(): void {
    this.searchFormFieldValue = '';
    this.business$$.next([]);
    this.hasMore$$.next(false);
    this.loadMore$$.next(false);
    this.loading$$.next(false);
    this.nextCursor$$.next('');
    this.previousSearchTerm = '';
  }
}
